import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../Button';
import { showErrorToast } from '../../lib/ToastUtility';
import { getKioskRegistration } from '../../services/KioskRegistrationService';
import { getApiErrorMessage } from '../../utils/apiError';
import type {
  KioskRegistrationAction,
  KioskRegistrationDetail,
} from '../../types/kioskRegistration.types';
import ModalShell from './ModalShell';
import RegistrationActionModal from './RegistrationActionModal';
import RegistrationAuditTrail from './RegistrationAuditTrail';
import RegistrationGatesList from './RegistrationGatesList';
import RegistrationStatusBadge from './RegistrationStatusBadge';
import {
  ACTION_LABELS,
  CHANNEL_TYPE_LABELS,
  formatCents,
  formatDateTime,
  TERMS_METHOD_LABELS,
  TYPE_LABELS,
} from './registrationUi';

interface Props {
  registrationId: number;
  onClose: () => void;
  onChanged: () => void;
}

const TOUR_STATUS_LABELS = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  DISMISSED: 'Skipped for now',
  COMPLETED: 'Completed',
} as const;

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="space-y-3">
    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h4>
    {children}
  </section>
);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="min-w-0">
    <dt className="text-xs font-semibold text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="break-words text-sm text-gray-800 dark:text-gray-100">{children || '—'}</dd>
  </div>
);

export default function RegistrationDetailDrawer({ registrationId, onClose, onChanged }: Props) {
  const [registration, setRegistration] = useState<KioskRegistrationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeAction, setActiveAction] = useState<KioskRegistrationAction | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setRegistration(await getKioskRegistration(registrationId));
    } catch (error) {
      const message = getApiErrorMessage(error, 'Unable to load this registration.');
      setLoadError(message);
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  }, [registrationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleActionDone = () => {
    setActiveAction(null);
    onChanged();
    void load();
  };

  const r = registration;

  return (
    <>
      <ModalShell
        title={r ? `${r.referenceCode} · ${r.organisationName || r.name}` : 'Registration'}
        variant="drawer"
        onClose={activeAction ? () => undefined : onClose}
      >
        {loading && !r && <p className="text-sm text-gray-600 dark:text-gray-300">Loading registration…</p>}
        {loadError && (
          <div role="alert" className="space-y-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            <p>{loadError}</p>
            <Button size="sm" variant="secondary" onClick={() => void load()}>Retry</Button>
          </div>
        )}
        {r && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <RegistrationStatusBadge status={r.status} channelDeleted={r.channelDeleted} />
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {TYPE_LABELS[r.registrationType] ?? r.registrationType} · via {r.source === 'ADMIN' ? `admin${r.createdByAdminEmail ? ` (${r.createdByAdminEmail})` : ''}` : 'website'}
              </span>
              {loading && <span className="text-xs text-gray-500">Refreshing…</span>}
            </div>

            {r.channelDeleted && (
              <p role="status" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
                The channel {r.kioskPublicId} created by this registration has been deleted, so the owner no longer has a working
                channel. They can register again from the website with the same email address.
              </p>
            )}

            <Section title="Actions">
              {r.availableActions.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No actions are available for this registration.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {r.availableActions.map((action) => (
                    <Button
                      key={action}
                      size="sm"
                      variant={action === 'REJECT' || action === 'CANCEL' ? 'secondary' : 'primary'}
                      onClick={() => setActiveAction(action)}
                    >
                      {ACTION_LABELS[action] ?? action}
                    </Button>
                  ))}
                </div>
              )}
            </Section>

            <Section title="Contact">
              <div className="flex gap-4">
                {r.profileImageUrl && (
                  <img
                    src={r.profileImageUrl}
                    alt={r.registrationType === 'CORPORATE' ? 'Company logo' : 'Display photo'}
                    className="h-20 w-20 shrink-0 rounded-xl border border-gray-200 bg-white object-contain dark:border-white/10"
                  />
                )}
                <dl className="grid flex-1 gap-3 sm:grid-cols-2">
                  <Field label={r.registrationType === 'CORPORATE' ? 'Contact person' : 'Name'}>{r.name}</Field>
                  <Field label="Organisation">{r.organisationName}</Field>
                  <Field label="Email">
                    <a className="text-emerald-800 hover:underline dark:text-emerald-300" href={`mailto:${r.email}`}>{r.email}</a>
                  </Field>
                  <Field label="Phone">{r.phone}</Field>
                  <Field label="Submitted">{formatDateTime(r.createdAt)}</Field>
                  <Field label="Owner password">{r.hasPassword || r.passwordSetByOwner ? 'Set' : 'Not set (owner uses “Forgot password”)'}</Field>
                  {r.linkExistingUser && <Field label="Account">Linked to an existing platform account</Field>}
                  {r.message && (
                    <div className="sm:col-span-2">
                      <Field label="Message"><span className="whitespace-pre-wrap">{r.message}</span></Field>
                    </div>
                  )}
                </dl>
              </div>
            </Section>

            {(r.referrerName || r.referrerEmail || r.referralSource) && (
              <Section title="Referrer">
                <dl className="grid gap-3 sm:grid-cols-3">
                  <Field label="Name">{r.referrerName}</Field>
                  <Field label="Email">{r.referrerEmail}</Field>
                  <Field label="Source">{r.referralSource}</Field>
                </dl>
              </Section>
            )}

            {r.riskFlags && r.riskFlags.length > 0 && (
              <Section title="Review flags">
                <div className="flex flex-wrap gap-2">
                  {r.riskFlags.map((flag) => (
                    <span key={flag} className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                      {flag.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </Section>
            )}

            <Section title="Commercial terms">
              <dl className="grid gap-3 sm:grid-cols-2">
                <Field label="Channel type">{r.channelType ? CHANNEL_TYPE_LABELS[r.channelType] : null}</Field>
                <Field label="Currency">{r.currencyCode}</Field>
                <Field label="Onboarding fee">{formatCents(r.onboardingFeeCents, r.currencyCode)}</Field>
                <Field label="Monthly fee">{formatCents(r.monthlyFeeCents, r.currencyCode)}</Field>
                <Field label="Call completed">{formatDateTime(r.callCompletedAt)}</Field>
                <Field label="Proposal sent">
                  {r.proposalSentAt ? `${formatDateTime(r.proposalSentAt)}${r.proposalSentByEmail ? ` by ${r.proposalSentByEmail}` : ''}` : null}
                </Field>
                {r.callNotes && (
                  <div className="sm:col-span-2">
                    <Field label="Call notes"><span className="whitespace-pre-wrap">{r.callNotes}</span></Field>
                  </div>
                )}
              </dl>
            </Section>

            <Section title="Terms & Conditions">
              <dl className="grid gap-3 sm:grid-cols-2">
                <Field label="Method">{r.termsAcceptedMethod ? TERMS_METHOD_LABELS[r.termsAcceptedMethod] : null}</Field>
                <Field label="Version">{r.termsVersion}</Field>
                <Field label="Accepted at">{formatDateTime(r.termsAcceptedAt)}</Field>
                <Field label="Accepted from IP">{r.termsAcceptedIp}</Field>
                <Field label="Document">
                  {r.termsDocumentUrl ? (
                    <a className="text-emerald-800 hover:underline dark:text-emerald-300" href={r.termsDocumentUrl} target="_blank" rel="noopener noreferrer">
                      Open document
                    </a>
                  ) : null}
                </Field>
                <Field label="Document reference">{r.termsDocumentReference}</Field>
              </dl>
            </Section>

            <Section title="Channel & billing">
              <dl className="grid gap-3 sm:grid-cols-2">
                <Field label="Linked channel">{r.kioskPublicId}</Field>
                <Field label="Setup invoice">{r.setupInvoiceId !== null && r.setupInvoiceId !== undefined ? `#${r.setupInvoiceId}` : null}</Field>
                <Field label="Payment received">{formatDateTime(r.paymentReceivedAt)}</Field>
                <Field label="Activated">{formatDateTime(r.activatedAt)}</Field>
                <Field label="Welcome email sent">{formatDateTime(r.welcomeEmailSentAt)}</Field>
                <Field label="Reviewed">
                  {r.reviewedAt ? `${formatDateTime(r.reviewedAt)}${r.reviewedByEmail ? ` by ${r.reviewedByEmail}` : ''}` : null}
                </Field>
                {r.reviewNote && (
                  <div className="sm:col-span-2">
                    <Field label="Review note"><span className="whitespace-pre-wrap">{r.reviewNote}</span></Field>
                  </div>
                )}
              </dl>
            </Section>

            {r.status === 'ACTIVE' && (
              <Section title="Owner onboarding tour">
                <dl className="grid gap-3 sm:grid-cols-2">
                  <Field label="Status">
                    {r.tour ? TOUR_STATUS_LABELS[r.tour.status] : 'Not opened yet (owner has not logged in)'}
                  </Field>
                  <Field label="Current step">{r.tour && r.tour.status !== 'COMPLETED' ? `Step ${r.tour.currentStep + 1}` : null}</Field>
                  <Field label="Started">{formatDateTime(r.tour?.startedAt)}</Field>
                  <Field label="Completed">{formatDateTime(r.tour?.completedAt)}</Field>
                  <Field label="Times skipped">{r.tour ? String(r.tour.dismissCount) : null}</Field>
                  <Field label="Last activity">{formatDateTime(r.tour?.lastActiveAt)}</Field>
                </dl>
              </Section>
            )}

            <Section title="Gates checklist">
              <RegistrationGatesList gates={r.gates} />
            </Section>

            <Section title="Audit trail">
              <RegistrationAuditTrail events={r.events} />
            </Section>
          </div>
        )}
      </ModalShell>

      {r && activeAction && (
        <RegistrationActionModal
          registration={r}
          action={activeAction}
          onClose={() => setActiveAction(null)}
          onDone={handleActionDone}
        />
      )}
    </>
  );
}
